import { AnimatedCheck, panelMotion } from "@pax/ui";
import { Check, Download } from "lucide-react";
import { useState } from "react";
import { Bubble } from "./Bubbles";
import { Failure, primaryButton } from "./components";

export type DownloadPart = { name: string; data: Blob | string };
export interface DownloadChoice {
  key: string;
  label: string;
  /** What the part contains, in a few words. */
  detail: string;
}

/**
 * Agents and evals are more than one thing to take away, so their download
 * asks what to include, then saves it all as one zip.
 */
export function DownloadMenu({
  title,
  name,
  choices,
  initial,
  load,
}: {
  title: string;
  name: string;
  choices: DownloadChoice[];
  initial: string[];
  load: (selected: string[]) => Promise<DownloadPart[]>;
}) {
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>();
  const picked = choices.filter(choice => selected.includes(choice.key));
  return (
    <Bubble
      label="Download"
      face={saved ? <Check className="size-4" /> : <Download className="size-4" />}
      align="right"
      icon
    >
      {close => (
        <div className="w-80 max-w-[calc(100vw-3rem)] p-2">
          <p className="px-2 pt-1 pb-2 text-sm font-medium">Download {title}</p>
          {choices.map(choice => {
            const checked = selected.includes(choice.key);
            return (
              <label
                key={choice.key}
                className={`flex cursor-pointer items-start gap-3 rounded-xl px-2 py-2 hover:bg-foreground/5 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-foreground/25 ${panelMotion}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() =>
                    setSelected(
                      checked
                        ? selected.filter(key => key !== choice.key)
                        : [...selected, choice.key],
                    )
                  }
                />
                <span className="mt-0.5">
                  <AnimatedCheck checked={checked} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm">{choice.label}</span>
                  <span className="block text-xs text-muted-foreground">{choice.detail}</span>
                </span>
              </label>
            );
          })}
          <Failure error={error} />
          <button
            type="button"
            className={`${primaryButton} mt-2 w-full`}
            disabled={busy || picked.length === 0}
            onClick={async () => {
              setBusy(true);
              setError(undefined);
              try {
                await saveZip(name, await load(picked.map(choice => choice.key)));
                close();
                setSaved(true);
                setTimeout(() => setSaved(false), 1500);
              } catch (error) {
                setError(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy
              ? "Preparing…"
              : picked.length === 0
                ? "Choose something to download"
                : `Download ${picked.length === 1 ? "1 item" : `${picked.length} items`}`}
          </button>
        </div>
      )}
    </Bubble>
  );
}

export const jsonPart = (name: string, value: unknown): DownloadPart => ({
  name: `${name}.json`,
  data: JSON.stringify(value, null, 2),
});
export async function filePart(name: string, url: string): Promise<DownloadPart> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not download ${name}.`);
  return { name, data: await response.blob() };
}
async function saveZip(name: string, files: DownloadPart[]) {
  const parts: BlobPart[] = [];
  const directory: BlobPart[] = [];
  let offset = 0;
  let directorySize = 0;
  for (const file of files) {
    const data = new Uint8Array(await new Blob([file.data]).arrayBuffer());
    const path = new TextEncoder().encode(file.name);
    let checksum = 0xffffffff;
    for (const byte of data) {
      checksum ^= byte;
      for (let bit = 0; bit < 8; bit++)
        checksum = (checksum >>> 1) ^ (checksum & 1 ? 0xedb88320 : 0);
    }
    checksum = (checksum ^ 0xffffffff) >>> 0;
    const header = new Uint8Array(30);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x04034b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 0x800, true);
    view.setUint32(14, checksum, true);
    view.setUint32(18, data.length, true);
    view.setUint32(22, data.length, true);
    view.setUint16(26, path.length, true);
    parts.push(header, path, data);
    const central = new Uint8Array(46);
    const index = new DataView(central.buffer);
    index.setUint32(0, 0x02014b50, true);
    index.setUint16(4, 20, true);
    index.setUint16(6, 20, true);
    index.setUint16(8, 0x800, true);
    index.setUint32(16, checksum, true);
    index.setUint32(20, data.length, true);
    index.setUint32(24, data.length, true);
    index.setUint16(28, path.length, true);
    index.setUint32(42, offset, true);
    directory.push(central, path);
    directorySize += central.length + path.length;
    offset += header.length + path.length + data.length;
  }
  const end = new Uint8Array(22);
  const view = new DataView(end.buffer);
  view.setUint32(0, 0x06054b50, true);
  view.setUint16(8, files.length, true);
  view.setUint16(10, files.length, true);
  view.setUint32(12, directorySize, true);
  view.setUint32(16, offset, true);
  const url = URL.createObjectURL(
    new Blob([...parts, ...directory, end], { type: "application/zip" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.zip`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
